import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CheckIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { DURATION_FAST, EASE_OUT } from '@/lib/motion';
import { cn } from '@/lib/utils';

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Secondary line shown below the label in the list (e.g. email). */
  sublabel?: string;
  /**
   * Optional image (S3 key or full URL). Ignored by the dropdown list; consumed
   * by wrappers like `EntityFilterSelect` that render a selected-item card.
   */
  imageUrl?: string | null;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  /** Empty string means "nothing selected" (the allLabel is shown). */
  value: string;
  onChange: (value: string) => void;
  /** Label for the "clear / all" first option.  Defaults to "All". */
  allLabel?: string;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  allLabel = 'All',
  loading = false,
  disabled = false,
  className = '',
  'data-testid': testId,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find((o) => o.value === value) ?? null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.sublabel?.toLowerCase().includes(q) ?? false)
    );
  }, [options, query]);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPanelStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 240),
      zIndex: 9999,
    });
  }, []);

  const handleOpen = () => {
    if (disabled || loading) return;
    updatePosition();
    setQuery('');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setQuery('');
  };

  const handleSelect = (val: string) => {
    onChange(val);
    handleClose();
    triggerRef.current?.focus();
  };

  // Auto-focus the search field after the panel enters
  useEffect(() => {
    if (open) {
      const id = setTimeout(() => searchRef.current?.focus(), 20);
      return () => clearTimeout(id);
    }
  }, [open]);

  // Close on outside mousedown (trigger and panel are both excluded)
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!triggerRef.current?.contains(t) && !panelRef.current?.contains(t)) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close when the PAGE scrolls so the panel stays aligned — but not when the
  // scroll happens inside the panel itself (scrolling the options list must
  // not dismiss the dropdown).
  useEffect(() => {
    if (!open) return;
    const handler = (e: Event) => {
      if (panelRef.current?.contains(e.target as Node)) return;
      handleClose();
    };
    window.addEventListener('scroll', handler, true);
    return () => window.removeEventListener('scroll', handler, true);
  }, [open]);

  // Escape closes and returns focus to the trigger
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  return (
    <>
      <div className={`relative ${className}`}>
        <button
          ref={triggerRef}
          type="button"
          onClick={open ? handleClose : handleOpen}
          disabled={disabled}
          aria-expanded={open}
          aria-haspopup="listbox"
          data-testid={testId}
          className={cn(
            'w-full flex items-center justify-between gap-2',
            'px-3.5 py-2.5 border border-[rgba(189,188,201,0.4)] rounded-full',
            'bg-background-primary text-sm text-left',
            'hover:border-border-primary/60 transition-colors duration-[120ms]',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 focus-visible:border-transparent',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            loading && 'cursor-wait opacity-70'
          )}
        >
          <span
            className={cn(
              'truncate',
              selectedOption ? 'text-text-primary' : 'text-text-secondary'
            )}
          >
            {loading ? 'Loading…' : selectedOption ? selectedOption.label : allLabel}
          </span>
          {loading ? (
            <span className="w-4 h-4 rounded-full border-2 border-primary-400 border-t-transparent animate-spin shrink-0" />
          ) : (
            <ChevronDownIcon
              className={cn(
                'w-4 h-4 text-text-secondary shrink-0 transition-transform duration-[120ms]',
                open && 'rotate-180'
              )}
            />
          )}
        </button>
      </div>

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              ref={panelRef}
              role="listbox"
              style={{ ...panelStyle, transformOrigin: 'top left' }}
              initial={{ opacity: 0, scale: 0.96, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: -4 }}
              transition={{ duration: DURATION_FAST + 0.03, ease: EASE_OUT }}
              className="bg-background-primary border border-[rgba(189,188,201,0.4)] rounded-xl shadow-2xl overflow-hidden"
            >
              {/* Search input */}
              <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border-primary/15">
                <MagnifyingGlassIcon className="w-4 h-4 text-text-tertiary shrink-0" />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search…"
                  className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-tertiary outline-none"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    className="p-0.5 rounded-full hover:bg-action-hover text-text-tertiary transition-colors"
                    aria-label="Clear search"
                  >
                    <XMarkIcon className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Option list */}
              <ul className="max-h-60 overflow-y-auto py-1">
                <li>
                  <button
                    type="button"
                    role="option"
                    aria-selected={value === ''}
                    onClick={() => handleSelect('')}
                    className={cn(
                      'w-full text-left px-3.5 py-2.5 text-sm transition-colors',
                      value === ''
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-350 font-medium'
                        : 'text-text-secondary hover:bg-action-hover'
                    )}
                  >
                    {allLabel}
                  </button>
                </li>

                {filtered.length === 0 ? (
                  <li className="px-3.5 py-3 text-sm text-text-tertiary italic">
                    No matches
                  </li>
                ) : (
                  filtered.map((opt) => (
                    <li key={opt.value}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={value === opt.value}
                        onClick={() => handleSelect(opt.value)}
                        className={cn(
                          'w-full text-left px-3.5 py-2.5 text-sm flex items-center justify-between gap-2 transition-colors',
                          value === opt.value
                            ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-350 font-medium'
                            : 'text-text-primary hover:bg-action-hover'
                        )}
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate">{opt.label}</span>
                          {opt.sublabel && (
                            <span className="block truncate text-xs text-text-tertiary font-normal">
                              {opt.sublabel}
                            </span>
                          )}
                        </span>
                        {value === opt.value && (
                          <CheckIcon className="w-4 h-4 shrink-0" />
                        )}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default SearchableSelect;
